import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { formatCurrency } from '../lib/format';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Download, FileText, DollarSign, Receipt, Calculator, Users, Calendar,
  TrendingUp, FileSpreadsheet, Archive, Building2
} from 'lucide-react';

export default function AccountantHandoverPage() {
  const { user } = useAuth();
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');

  // Fetch company name
  useEffect(() => {
    api.get('/account').then(r => setCompanyName(r.data.company_name)).catch(() => {});
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/reports/year-end?start_date=${startDate}&end_date=${endDate}`);
      setData(res.data);
      toast.success('Report generated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const setFiscalYear = (year) => {
    setStartDate(`${year}-01-01`);
    setEndDate(`${year}-12-31`);
  };

  const downloadCSV = async (type) => {
    try {
      const res = await api.get(`/reports/export/${type}-csv?start_date=${startDate}&end_date=${endDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_${startDate}_to_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} CSV downloaded`);
    } catch {
      toast.error('Download failed');
    }
  };

  const downloadPDF = async () => {
    try {
      const res = await api.get(`/reports/export/year-end-pdf?start_date=${startDate}&end_date=${endDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `year_end_${startDate}_to_${endDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch {
      toast.error('PDF download failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-testid="handover-title">
          Accountant Handover
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Generate organized year-end data packages for your accountant
        </p>
      </div>

      {/* Period Selector Card */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          {companyName && (
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">{companyName}</span>
            </div>
          )}
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Period Start</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="w-44"
                data-testid="handover-start-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Period End</Label>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="w-44"
                data-testid="handover-end-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600">Fiscal Year Quick Select</Label>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiscalYear(currentYear - 1)}
                  data-testid="fiscal-year-prev"
                >
                  {currentYear - 1}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFiscalYear(currentYear)}
                  data-testid="fiscal-year-current"
                >
                  {currentYear}
                </Button>
              </div>
            </div>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={fetchData}
              disabled={loading}
              data-testid="generate-report"
            >
              <Calendar className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {!data && !loading && (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-slate-700 mb-2">Select a Period & Generate</h2>
            <p className="text-sm text-slate-500">
              Choose a date range and click Generate to view your year-end summary
            </p>
          </CardContent>
        </Card>
      )}

      {data && (
        <div className="space-y-6">
          {/* Reporting Period Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Reporting Period</p>
                  <p className="text-xs text-blue-700">{companyName}</p>
                </div>
              </div>
              <p className="text-lg font-semibold text-blue-900">
                {startDate} to {endDate}
              </p>
            </div>
          </div>

          {/* 1. Accountant Handover Summary */}
          <section data-testid="handover-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              1. Accountant Handover Summary
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard
                icon={DollarSign}
                label="Total Invoiced"
                value={formatCurrency(data.income.total_invoiced)}
                color="text-blue-600 bg-blue-50"
              />
              <KPICard
                icon={DollarSign}
                label="Total Collected"
                value={formatCurrency(data.payments.total_payments)}
                color="text-emerald-600 bg-emerald-50"
              />
              <KPICard
                icon={Receipt}
                label="Total Expenses"
                value={formatCurrency(data.expenses.total_expenses)}
                color="text-rose-600 bg-rose-50"
              />
              <KPICard
                icon={Calculator}
                label="Tax Collected"
                value={formatCurrency(data.tax.total_tax_collected)}
                color="text-amber-600 bg-amber-50"
              />
            </div>
          </section>

          <Separator />

          {/* 2. Income Summary */}
          <section data-testid="income-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              2. Income Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <SummaryCard
                title="Income Overview"
                items={[
                  { label: 'Total Invoiced', value: formatCurrency(data.income.total_invoiced) },
                  { label: 'Total Collected', value: formatCurrency(data.income.total_collected) },
                  { label: 'Outstanding', value: formatCurrency(data.income.total_outstanding) },
                  { label: 'Invoices Issued', value: data.income.invoice_count },
                  { label: 'Invoices Paid', value: data.income.paid_invoice_count },
                ]}
              />
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-600">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadCSV('invoices')}
                    data-testid="export-invoices-csv"
                    className="w-full"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download Invoices CSV
                  </Button>
                </CardContent>
              </Card>
            </div>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Issue Date</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.income.invoices.slice(0, 10).map((inv, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>
                          <Badge
                            className={`text-xs ${
                              inv.status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}
                          >
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{inv.issue_date}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.total)}</TableCell>
                        <TableCell className="text-right text-emerald-600">
                          {formatCurrency(inv.paid)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(inv.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {data.income.invoices.length > 10 && (
                  <div className="p-3 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
                    Showing 10 of {data.income.invoices.length} invoices. Download CSV for complete list.
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* 3. Expense Summary */}
          <section data-testid="expense-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-rose-600" />
              3. Expense Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <SummaryCard
                title="Expense Overview"
                items={[
                  { label: 'Total Expenses', value: formatCurrency(data.expenses.total_expenses) },
                  { label: 'Tax on Expenses', value: formatCurrency(data.expenses.total_tax) },
                  ...data.expenses.by_category.map(c => ({ label: c.category, value: formatCurrency(c.total) })),
                ]}
              />
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-600">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadCSV('expenses')}
                    data-testid="export-expenses-csv"
                    className="w-full"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download Expenses CSV
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* 4. Accounts Receivable Summary */}
          <section data-testid="ar-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              4. Accounts Receivable Summary
            </h2>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Outstanding Invoices</CardTitle>
                  <span className="text-2xl font-bold text-slate-900">
                    {formatCurrency(data.ar.total_outstanding)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{data.ar.invoice_count} unpaid invoices</p>
              </CardHeader>
              <CardContent className="p-0">
                {data.ar.invoices.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No outstanding invoices</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.ar.invoices.map((inv, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                          <TableCell>
                            <Badge
                              className={`text-xs ${
                                inv.status === 'overdue'
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}
                            >
                              {inv.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{inv.due_date || '-'}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(inv.balance_due)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* 5. Payment Summary */}
          <section data-testid="payment-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              5. Payment Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <SummaryCard
                title="Payment Overview"
                items={[
                  { label: 'Total Payments', value: formatCurrency(data.payments.total_payments) },
                  { label: 'Payment Count', value: data.payments.payment_count },
                  ...data.payments.by_method.map(m => ({
                    label: m.method.replace('_', ' '),
                    value: formatCurrency(m.total),
                  })),
                ]}
              />
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium text-slate-600">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadCSV('payments')}
                    data-testid="export-payments-csv"
                    className="w-full"
                  >
                    <Download className="w-3 h-3 mr-1" /> Download Payments CSV
                  </Button>
                </CardContent>
              </Card>
            </div>
          </section>

          <Separator />

          {/* 6. GST / PST Summary */}
          <section data-testid="tax-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-amber-600" />
              6. GST / PST Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">GST Collected</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(data.tax.total_gst_collected)}</p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">PST Collected</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(data.tax.total_pst_collected)}</p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <p className="text-xs text-slate-500">Net GST (collected - expenses)</p>
                  <p className="text-2xl font-bold text-slate-900">{formatCurrency(data.tax.net_gst)}</p>
                </CardContent>
              </Card>
            </div>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-medium">Tax Detail</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCSV('tax-summary')}
                  data-testid="export-tax-csv"
                >
                  <Download className="w-3 h-3 mr-1" /> Download CSV
                </Button>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">PST</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tax.invoices_with_tax.slice(0, 10).map((inv, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell className="text-sm">{inv.issue_date}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.gst)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(inv.pst)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(inv.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {data.tax.invoices_with_tax.length > 10 && (
                  <div className="p-3 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
                    Showing 10 of {data.tax.invoices_with_tax.length} tax entries. Download CSV for complete list.
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* 7. Client / Project Summary */}
          <section data-testid="client-project-summary-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              7. Client / Project Summary
            </h2>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Client Directory</CardTitle>
                  <span className="text-sm text-slate-500">
                    {data.clients.total_clients} clients, {data.clients.total_projects} projects
                  </span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>City</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.clients.clients.map((c, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{c.name}</TableCell>
                        <TableCell>{c.email || '-'}</TableCell>
                        <TableCell>{c.city || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </section>

          <Separator />

          {/* 8. Export Package */}
          <section data-testid="export-package-section">
            <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Archive className="w-5 h-5 text-slate-600" />
              8. Export Package
            </h2>
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium">Complete Data Export</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Download all financial data for the selected period in various formats
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => downloadCSV('invoices')}
                    data-testid="export-package-invoices"
                    className="justify-start"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                    Invoice Archive (CSV)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadCSV('expenses')}
                    data-testid="export-package-expenses"
                    className="justify-start"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-rose-600" />
                    Expense Export (CSV)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => downloadCSV('payments')}
                    data-testid="export-package-payments"
                    className="justify-start"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-blue-600" />
                    Payment Export (CSV)
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadPDF}
                    data-testid="export-package-pdf"
                    className="justify-start"
                  >
                    <FileText className="w-4 h-4 mr-2 text-amber-600" />
                    Year-End Report (PDF)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <p className="text-xs text-slate-500">{label}</p>
            <p className="text-lg font-semibold text-slate-900">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCard({ title, items }) {
  return (
    <Card className="border border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2.5">
          {items.map((item, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-sm text-slate-600 capitalize">{item.label}</span>
              <span className="text-sm font-medium text-slate-900">{item.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
